<script setup lang="ts">
import { useSearchHistoryStore } from '../../stores/searchHistoryStore'
import { useVehicleStore } from '../../stores/vehicleStore'
import { usePartsStore } from '../../stores/partsStore'

const historyStore = useSearchHistoryStore()
const vehicleStore = useVehicleStore()
const partsStore = usePartsStore()

async function selectEntry(entry: { type: string; query: string; carId: number | null }) {
  if (entry.type === 'regnr') {
    vehicleStore.searchMode = 'regnr'
    await vehicleStore.searchByRegNr(entry.query)
  } else if (entry.type === 'manual' && entry.carId) {
    vehicleStore.searchMode = 'manual'
    await vehicleStore.selectMotor(entry.carId)
  } else if (entry.type === 'oem') {
    vehicleStore.searchMode = 'oem'
    vehicleStore.oemNumber = entry.query
    await partsStore.loadOemParts(entry.query)
  } else if (entry.type === 'motorcode' && entry.carId) {
    vehicleStore.searchMode = 'motorcode'
    await vehicleStore.selectMotor(entry.carId)
  }
}

function removeEntry(type: string, query: string, e: Event) {
  e.stopPropagation()
  historyStore.removeEntry(type, query)
}

function typeLabel(type: string): string {
  switch (type) {
    case 'regnr': return 'Regnr'
    case 'manual': return 'Modell'
    case 'oem': return 'OE'
    case 'motorcode': return 'Motor'
    default: return type
  }
}

function formatTime(timestamp: number): string {
  const d = new Date(timestamp)
  return d.toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit' })
}
</script>

<template>
  <div v-if="historyStore.entries.length > 0" class="search-history">
    <p class="search-history__title">Søkehistorikk</p>
    <ul class="search-history__list">
      <li
        v-for="entry in historyStore.entries"
        :key="`${entry.type}-${entry.query}`"
        class="search-history__item"
        @click="selectEntry(entry)"
      >
        <span class="search-history__type">{{ typeLabel(entry.type) }}</span>
        <span class="search-history__query">{{ entry.query }}</span>
        <span class="search-history__name">{{ entry.carName }}</span>
        <span class="search-history__date">{{ formatTime(entry.timestamp) }}</span>
        <button
          class="search-history__remove"
          @click="removeEntry(entry.type, entry.query, $event)"
          aria-label="Fjern"
        >
          &times;
        </button>
      </li>
    </ul>
  </div>
</template>

<style scoped lang="scss">
.search-history {
  margin-top: 1rem;
  text-align: left;

  &__title {
    font-size: 0.85rem;
    color: #666;
    margin-bottom: 0.4rem;
  }

  &__list {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.85rem;

    &:hover {
      background: #f5f5f5;
    }
  }

  &__type {
    background: #002868;
    color: white;
    font-size: 0.7rem;
    padding: 1px 6px;
    border-radius: 4px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    flex-shrink: 0;
  }

  &__query {
    font-weight: 600;
    font-family: monospace;
    letter-spacing: 0.5px;
    flex-shrink: 0;
  }

  &__name {
    flex: 1;
    color: #555;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__date {
    color: #999;
    font-size: 0.75rem;
    flex-shrink: 0;
  }

  &__remove {
    background: none;
    border: none;
    color: #999;
    font-size: 1.2rem;
    cursor: pointer;
    padding: 0 4px;
    line-height: 1;
    flex-shrink: 0;

    &:hover { color: #c00; }
  }
}
</style>
